import { useCallback, useMemo, useState } from 'react';
import { formatCurrency } from './subscription.utils';
import { formatCurrencyWithLocale } from '@utils/math';
import { BillingPeriod } from '@generated/graphql';
import type {
    ConnectorSummary,
    ConnectorFeeBreakdown,
    SubscriptionOrgFragment,
} from './subscription.calculations';
import {
    normalizeConnectorFeeInput,
    calculateAddonPricing,
    calculateBasePricing,
    calculateConnectorMetadata,
    getNextInvoiceLabel,
    partitionSubscriptionLines,
    calculateSubscriptionTimeline,
} from './subscription.calculations';
export type {
    ConnectorSummary,
    ConnectorFeeBreakdown,
    SubscriptionOrgFragment,
} from './subscription.calculations';

export interface ChargeSystemSubscriptionState {
    activeConnectors: string[];
    connectorCount: number;
    numberOfInactiveConnectors: number;
}

export interface UseSubscriptionCalculationsResult {
    showVAT: boolean;
    setShowVAT: (v: boolean) => void;
    hasData: boolean;
    billingPeriod: BillingPeriod | null;

    // EXISTING‐FLOW
    activeConnectors: string[];
    paidConnectors: number;
    numberOfInactiveConnectors: number;
    productPriceId: string;
    formattedDisplayTotal: string;
    formattedMonthlyPerConnector: string;
    lanSummary: ConnectorSummary;
    simSummary: ConnectorSummary | null;

    // SELECT‐FLOW
    countNewConnectors: (selectedIds: string[]) => number;
    formatMonthlyFee: (input: number | ConnectorFeeBreakdown) => string;
    formatYearlyFee: (input: number | ConnectorFeeBreakdown) => string;

    // NEXT‐INVOICE
    nextInvoiceLabel: string;
    displayConnectorCount: number;

    // CHARGE-SYSTEM
    getChargeSystemSubscriptionState: (chargeSystemId: string) => ChargeSystemSubscriptionState;
}

export const useSubscriptionCalculations = (
    organization?: SubscriptionOrgFragment
): UseSubscriptionCalculationsResult => {
    const [showVAT, setShowVAT] = useState(false);

    const hasData = Boolean(organization);

    // COMMON
    const { lanConnectorIds, connectorCount, hardwareSimConnectorIds } = useMemo(
        () => calculateConnectorMetadata(organization),
        [organization?.chargeSystems]
    );
    const subscription = organization?.subscription;
    const billingPeriod = subscription?.billingPeriod ?? null;
    const { nextInvoiceDate, calculationDate } = useMemo(
        () => calculateSubscriptionTimeline(subscription),
        [subscription]
    );
    const { subscriptionLines, baseLine, addonLines, baseLineConnectorIds } = useMemo(
        () => partitionSubscriptionLines(subscription?.subscriptionLines, calculationDate),
        [subscription?.subscriptionLines, calculationDate]
    );
    const {
        activeConnectors,
        paidConnectors,
        numberOfInactiveConnectors,
        productPriceId,
        perConnectorEx,
        perConnectorInc,
        currencyCode,
        baseMonthlyEx,
        baseMonthlyInc,
    } = useMemo(
        () =>
            calculateBasePricing({
                baseLine,
                connectorCount,
                subscriptionLines,
            }),
        [baseLine, connectorCount, subscriptionLines]
    );
    const {
        addonTotals,
        simSummary: rawSimSummary,
        simPerConnectorEx,
        simPerConnectorInc,
        simConnectorIds,
    } = useMemo(
        () =>
            calculateAddonPricing({
                addonLines,
                calculationDate,
                currencyCode,
                showVAT,
            }),
        [addonLines, calculationDate, currencyCode, showVAT]
    );

    const lanConnectorsSet = useMemo(() => new Set(baseLineConnectorIds), [baseLineConnectorIds]);

    // Only exclude SIM hardware from LAN counts when there is a SIM add-on line (legacy single-line
    // subscriptions should treat SIM hardware as part of the base line).
    const excludeSimFromLan = Boolean(rawSimSummary);

    const lanExclusionSet = useMemo(() => {
        const union = new Set<string>();
        if (excludeSimFromLan) {
            hardwareSimConnectorIds.forEach((id) => union.add(id));
        }
        simConnectorIds.forEach((id) => union.add(id));
        return union;
    }, [excludeSimFromLan, hardwareSimConnectorIds, simConnectorIds]);

    const lanActiveCount = useMemo(
        () =>
            activeConnectors.filter((cid) => lanConnectorsSet.has(cid) && !lanExclusionSet.has(cid))
                .length,
        [activeConnectors, lanConnectorsSet, lanExclusionSet]
    );
    const lanTotalCount = useMemo(() => {
        if (excludeSimFromLan) {
            return lanConnectorIds.filter((cid) => !simConnectorIds.has(cid)).length;
        }
        return lanConnectorIds.length + hardwareSimConnectorIds.size;
    }, [excludeSimFromLan, hardwareSimConnectorIds.size, lanConnectorIds, simConnectorIds]);

    const simHardwareActiveCount = useMemo(
        () => activeConnectors.filter((cid) => hardwareSimConnectorIds.has(cid)).length,
        [activeConnectors, hardwareSimConnectorIds]
    );

    const simDisplayActive = simHardwareActiveCount;
    const simDisplayTotal = hardwareSimConnectorIds.size;

    const basePerConnectorDisplay = showVAT ? perConnectorInc : perConnectorEx;
    const simAddonPerConnectorDisplay = showVAT ? simPerConnectorInc : simPerConnectorEx;
    const simSummary = useMemo(() => {
        if (!rawSimSummary) return null;
        const formattedPerConnector = currencyCode
            ? formatCurrency(basePerConnectorDisplay + simAddonPerConnectorDisplay, currencyCode)
            : rawSimSummary.formattedPerConnector;
        return {
            ...rawSimSummary,
            active: simDisplayActive,
            total: simDisplayTotal,
            formattedPerConnector,
        };
    }, [
        rawSimSummary,
        simDisplayActive,
        simDisplayTotal,
        currencyCode,
        basePerConnectorDisplay,
        simAddonPerConnectorDisplay,
    ]);

    const totalMonthlyEx = baseMonthlyEx + addonTotals.ex;
    const totalMonthlyInc = baseMonthlyInc + addonTotals.inc;
    const displayTotal = showVAT ? totalMonthlyInc : totalMonthlyEx;
    const displayConnectorCount = activeConnectors.length || connectorCount;

    const perConnectorDisplayEx =
        displayConnectorCount > 0 ? totalMonthlyEx / displayConnectorCount : perConnectorEx;
    const perConnectorDisplayInc =
        displayConnectorCount > 0 ? totalMonthlyInc / displayConnectorCount : perConnectorInc;

    const formattedDisplayTotal = formatCurrency(displayTotal, currencyCode);
    const formattedMonthlyPerConnector = formatCurrency(
        showVAT ? perConnectorDisplayInc : perConnectorDisplayEx,
        currencyCode
    );

    const lanSummary: ConnectorSummary = {
        active: lanActiveCount,
        total: lanTotalCount,
        formattedPerConnector: formatCurrency(
            showVAT ? perConnectorInc : perConnectorEx,
            currencyCode
        ),
    };

    // SELECT-FLOW
    const countNewConnectors = (selectedIds: string[]) =>
        selectedIds.filter((cid) => !activeConnectors.includes(cid)).length;

    const calculateMonthlyFee = (input: number | ConnectorFeeBreakdown) => {
        const { lan, sim } = normalizeConnectorFeeInput(input);
        const monthlyBase = showVAT ? perConnectorInc : perConnectorEx;
        const monthlySimAddon = showVAT ? simPerConnectorInc : simPerConnectorEx;
        const lanTotal = lan * monthlyBase;
        const simTotal = sim * (monthlyBase + monthlySimAddon);
        return lanTotal + simTotal;
    };

    const calculateYearlyFee = (input: number | ConnectorFeeBreakdown) =>
        calculateMonthlyFee(input) * 12;

    const formatMonthlyFee = (input: number | ConnectorFeeBreakdown) =>
        currencyCode
            ? formatCurrencyWithLocale(calculateMonthlyFee(input), navigator.language, currencyCode)
            : 'N/A';

    const formatYearlyFee = (input: number | ConnectorFeeBreakdown) =>
        currencyCode
            ? formatCurrencyWithLocale(calculateYearlyFee(input), navigator.language, currencyCode)
            : 'N/A';

    const nextInvoiceLabel = useMemo(
        () => getNextInvoiceLabel(subscription, calculationDate, nextInvoiceDate),
        [subscription, calculationDate, nextInvoiceDate]
    );

    const getChargeSystemSubscriptionState = useCallback(
        (chargeSystemId: string): ChargeSystemSubscriptionState => {
            const chargeSystem = organization?.chargeSystems?.find(
                (candidate) => candidate?.id === chargeSystemId
            );
            const connectorIds =
                chargeSystem?.connectors
                    ?.map((connector) => connector?.id)
                    .filter((connectorId): connectorId is string => Boolean(connectorId)) ?? [];

            if (connectorIds.length === 0) {
                return {
                    activeConnectors: [],
                    connectorCount: 0,
                    numberOfInactiveConnectors: 0,
                };
            }

            const chargeSystemConnectorIds = new Set(connectorIds);
            const chargeSystemActiveConnectors = activeConnectors.filter((connectorId) =>
                chargeSystemConnectorIds.has(connectorId)
            );

            return {
                activeConnectors: chargeSystemActiveConnectors,
                connectorCount: connectorIds.length,
                numberOfInactiveConnectors: Math.max(
                    connectorIds.length - chargeSystemActiveConnectors.length,
                    0
                ),
            };
        },
        [organization?.chargeSystems, activeConnectors]
    );

    return {
        showVAT,
        setShowVAT,
        hasData,
        billingPeriod,

        // EXISTING-FLOW
        activeConnectors,
        paidConnectors,
        numberOfInactiveConnectors,
        productPriceId,
        formattedDisplayTotal,
        formattedMonthlyPerConnector,
        lanSummary,
        simSummary,

        // SELECT-FLOW
        countNewConnectors,
        formatMonthlyFee,
        formatYearlyFee,

        // NEXT-INVOICE
        nextInvoiceLabel,
        displayConnectorCount,

        // CHARGE-SYSTEM
        getChargeSystemSubscriptionState,
    };
};
